import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSocket } from "../lib/useSocket";
import {
    isCustomerMessage,
    isShopperTypingUser,
    normalizeMessage,
} from "./chat/chatUtils";
import { IoCartOutline, IoCopyOutline, IoOpenOutline } from "react-icons/io5";

const RICH_MESSAGE_LABELS = {
    product_card_list: "Shared products with you",
    copiable_text: "Sent you a code",
    button_link: "Sent you a link",
};

function TypingDots() {
    return (
        <div className="tw-flex tw-items-center tw-gap-1">
            <div className="tw-flex tw-space-x-1">
                {[0, 150, 300].map((delay) => (
                    <div
                        key={delay}
                        className="tw-w-1.5 tw-h-1.5 tw-bg-white/70 tw-rounded-full tw-animate-bounce"
                        style={{ animationDelay: `${delay}ms` }}
                    />
                ))}
            </div>
            <span className="tw-text-[11px] tw-text-white/80 tw-ml-1">
                typing
            </span>
        </div>
    );
}

/**
 * ChatNotification Component
 * Glass-style floating notification that matches the ChatBubble aesthetic
 */
export function ChatNotification({ isOpen, darkMode = false, onClick }) {
    const [isVisible, setIsVisible] = useState(false);
    const [messageData, setMessageData] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const { onMessageNew, onUserOnline, onTypingStart, onTypingStop } =
        useSocket();
    const timerRef = useRef(null);
    const typingTimerRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(false);
            setIsTyping(false);
            if (timerRef.current) clearTimeout(timerRef.current);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleNewMessage = (msg) => {
            if (isOpen || isCustomerMessage(msg)) return;

            // New message clears typing state
            setIsTyping(false);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);

            const normalized = normalizeMessage(msg);
            if (normalized) {
                setMessageData(normalized);
                setIsVisible(true);

                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setIsVisible(false);
                }, 5000);
            }
        };

        const handleUserOnline = (payload) => {
            if (isOpen || !payload?.user) return;

            const user = payload.user;
            setMessageData({
                senderName: user.name,
                senderAvatar: user.avatar,
                text: "Is now online",
                isOnlineNotification: true,
            });
            setIsVisible(true);

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setIsVisible(false);
            }, 5000);
        };

        const handleTypingStart = (payload) => {
            if (isOpen) return;
            // Only staff/AI typing — ignore shopper echo (including ourselves).
            if (payload?.user && isShopperTypingUser(payload.user)) return;
            const name = payload?.user?.name || payload?.name || "Agent";
            setTypingUser(name);
            setIsTyping(true);

            // Auto-clear typing after 5s if no stop event
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
            typingTimerRef.current = setTimeout(() => {
                setIsTyping(false);
            }, 5000);
        };

        const handleTypingStop = (payload) => {
            if (payload?.user && isShopperTypingUser(payload.user)) return;
            setIsTyping(false);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        };

        const cleanupMsg = onMessageNew(handleNewMessage);
        const cleanupOnline = onUserOnline(handleUserOnline);
        const cleanupTypingStart = onTypingStart(handleTypingStart);
        const cleanupTypingStop = onTypingStop(handleTypingStop);

        return () => {
            cleanupMsg();
            cleanupOnline();
            cleanupTypingStart();
            cleanupTypingStop();
            if (timerRef.current) clearTimeout(timerRef.current);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        };
    }, [isOpen, onMessageNew, onUserOnline, onTypingStart, onTypingStop]);

    const getRichIcon = (messageType) => {
        switch (messageType) {
            case "product_card_list":
                return (
                    <IoCartOutline
                        size={13}
                        className="tw-shrink-0 tw-text-white/70"
                    />
                );
            case "copiable_text":
                return (
                    <IoCopyOutline
                        size={13}
                        className="tw-shrink-0 tw-text-white/70"
                    />
                );
            case "button_link":
                return (
                    <IoOpenOutline
                        size={13}
                        className="tw-shrink-0 tw-text-white/70"
                    />
                );
            default:
                return null;
        }
    };

    const getDisplayText = (data) => {
        if (data.isOnlineNotification) return data.text;
        const richLabel = RICH_MESSAGE_LABELS[data.messageType];
        if (richLabel && !data.text) return richLabel;
        return data.text || richLabel || "New message";
    };

    const showNotification = isVisible && !isOpen && messageData;
    const showTyping = isTyping && !isOpen && !showNotification;

    // Match the launcher bubble: translucent glass over a dark page, brand
    // colour over a light one — a white glass surface vanishes on white sites.
    const surfaceClass = darkMode
        ? "tw-bg-white/10 tw-border-white/20"
        : "tw-bg-primary/90 tw-border-white/30";
    const arrowClass = darkMode
        ? "tw-border-l-white/20"
        : "tw-border-l-primary/90";

    return (
        <AnimatePresence>
            {/* Typing indicator */}
            {showTyping && (
                <motion.div
                    key="typing"
                    initial={{ opacity: 0, x: 15, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 15, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="tw-absolute tw-bottom-0 tw-top-[10px] tw-right-full tw-mr-3 tw-cursor-pointer tw-z-50"
                    onClick={() => {
                        if (onClick) onClick();
                    }}
                >
                    <div
                        className={`tw-px-4 tw-py-2.5 tw-rounded-2xl tw-border tw-shadow-lg tw-shadow-black/20 ${surfaceClass}`}
                        style={{
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                        }}
                    >
                        <div className="tw-flex tw-items-center tw-gap-2.5">
                            {typingUser && (
                                <span className="tw-text-[11px] tw-font-semibold tw-text-white/80 tw-whitespace-nowrap">
                                    {typingUser}
                                </span>
                            )}
                            <TypingDots />
                        </div>
                    </div>
                    {/* Arrow */}
                    <div className={`tw-absolute tw-top-1/2 tw--right-1.5 tw--mt-1.5 tw-w-0 tw-h-0 tw-border-solid tw-border-r-0 tw-border-t-[5px] tw-border-t-transparent tw-border-b-[5px] tw-border-b-transparent tw-border-l-[6px] ${arrowClass}`} />
                </motion.div>
            )}

            {/* Message notification */}
            {showNotification && (
                <motion.div
                    key="notification"
                    initial={{ opacity: 0, x: 15, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 15, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="tw-absolute tw-top-[-2px] tw-bottom-1 tw-right-full tw-mr-3 tw-w-60 tw-cursor-pointer tw-z-50"
                    onClick={() => {
                        if (onClick) onClick();
                    }}
                >
                    <div
                        className={`tw-p-3 tw-rounded-2xl tw-border tw-shadow-lg tw-shadow-black/20 ${surfaceClass}`}
                        style={{
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                        }}
                    >
                        <div className="tw-flex tw-items-start tw-gap-2.5">
                            <div className="tw-shrink-0 tw-w-8 tw-h-8">
                                {messageData.senderAvatar ? (
                                    <img
                                        src={messageData.senderAvatar}
                                        alt={messageData.senderName || "Avatar"}
                                        className="tw-w-full tw-h-full tw-rounded-full tw-object-cover tw-border tw-border-white/20"
                                    />
                                ) : (
                                    <div className="tw-w-full tw-h-full tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-white/15 tw-border tw-border-white/10">
                                        <span className="tw-text-xs tw-font-bold tw-text-white/80">
                                            {(messageData.senderName || "?")
                                                .charAt(0)
                                                .toUpperCase()}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="tw-flex-1 tw-min-w-0">
                                <div className="tw-mb-0.5">
                                    <span className="tw-text-[11px] tw-font-bold tw-text-white/90 tw-truncate tw-block">
                                        {messageData.senderName ||
                                            "New Message"}
                                    </span>
                                </div>
                                <div
                                    className={`tw-text-[11px] tw-line-clamp-2 tw-break-words tw-flex tw-items-center tw-gap-1.5 ${
                                        messageData.isOnlineNotification
                                            ? "tw-text-green-300 tw-font-medium"
                                            : "tw-text-white/80"
                                    }`}
                                >
                                    {getRichIcon(messageData.messageType)}
                                    <span>{getDisplayText(messageData)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Arrow */}
                    <div className={`tw-absolute tw-top-1/2 tw--right-1.5 tw--mt-1.5 tw-w-0 tw-h-0 tw-border-solid tw-border-r-0 tw-border-t-[5px] tw-border-t-transparent tw-border-b-[5px] tw-border-b-transparent tw-border-l-[6px] ${arrowClass}`} />
                </motion.div>
            )}
        </AnimatePresence>
    );
}
